import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Download, RefreshCw, AlertCircle, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';
import { ExportButtons } from './ExportButtons';
import { csvCell } from '../utils/csvSafe';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FlowRow {
  from_facility_code: string;
  from_facility_name: string;
  to_facility_code: string;
  to_facility_name: string;
  to_facility_type: string;
  to_company_owned: string;
  material_group: string;
  total_tons: string;
}

interface Filters {
  year: string;
  month: string;
  facility: string;
  material_group: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = [
  { value: '1',  label: 'January'   }, { value: '2',  label: 'February'  },
  { value: '3',  label: 'March'     }, { value: '4',  label: 'April'     },
  { value: '5',  label: 'May'       }, { value: '6',  label: 'June'      },
  { value: '7',  label: 'July'      }, { value: '8',  label: 'August'    },
  { value: '9',  label: 'September' }, { value: '10', label: 'October'   },
  { value: '11', label: 'November'  }, { value: '12', label: 'December'  },
];

const MATERIAL_GROUPS = ['Disposal', 'Recycle', 'Reuse', 'Land Application'];

const now = new Date();
const CURRENT_YEAR = String(now.getFullYear());
const CURRENT_MONTH = String(now.getMonth() + 1);

function toNum(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

export function FacilityFlow() {
  const { getToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [filters, setFilters] = useState<Filters>({
    year: CURRENT_YEAR, month: CURRENT_MONTH, facility: '', material_group: '',
  });

  const [facilities, setFacilities] = useState<{ facility_code: string; facility_name: string }[]>([]);
  const [data, setData] = useState<FlowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/reports/generate`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ report_id: 'lookup.facilities', params: {} }),
        });
        const result = await res.json();
        setFacilities(result.data ?? []);
      } catch (e) {
        console.error('Failed to load facilities:', e);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params: Record<string, string> = { year: filters.year, month: filters.month };
      if (filters.facility) params.facility = filters.facility;
      if (filters.material_group) params.material_group = filters.material_group;

      const res = await fetch(`${API_URL}/reports/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_id: 'dashboard.facility_flow', params }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result.data ?? []);
      setHasLoaded(true);
    } catch (e: any) {
      setError(e.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map(r => headers.map(h => csvCell((r as any)[h])).join(','));
    const csv = [headers.map(csvCell).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facility-flow-${filters.year}-${filters.month.padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalTons = data.reduce((s, r) => s + toNum(r.total_tons), 0);
  const companyOwnedTons = data.filter(r => r.to_company_owned === 'Y').reduce((s, r) => s + toNum(r.total_tons), 0);
  const thirdPartyTons = totalTons - companyOwnedTons;
  const destinationCount = new Set(data.map(r => r.to_facility_code)).size;

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-cyan-600 rounded-lg">
              <ArrowRightLeft className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Facility-to-Facility Flow</h1>
              <p className="text-sm text-gray-500">Where material moves between your facilities and third parties</p>
            </div>
          </div>
{hasLoaded && data.length > 0 && (
  <div className="flex items-center space-x-2">
    <button onClick={exportCSV}
      className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-colors">
      <Download className="w-4 h-4" /><span>Export CSV</span>
    </button>
    <ExportButtons
      reportTitle="Facility-to-Facility Flow"
      subtitle={`${MONTHS.find(m => m.value === filters.month)?.label ?? ''} ${filters.year}`}
      getExportData={() => ({ columns: Object.keys(data[0]), data: data as unknown as Record<string, string>[] })}

    />
  </div>
)}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Year <span className="text-red-500">*</span></label>
              <input type="number" value={filters.year} min="2020" max={Number(CURRENT_YEAR)}
                onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Month <span className="text-red-500">*</span></label>
              <select value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From Facility</label>
              <select value={filters.facility} onChange={e => setFilters(f => ({ ...f, facility: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                <option value="">All Facilities</option>
                {facilities.map(fc => <option key={fc.facility_code} value={fc.facility_code}>{fc.facility_name || fc.facility_code}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Material Group</label>
              <select value={filters.material_group} onChange={e => setFilters(f => ({ ...f, material_group: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                <option value="">All Groups</option>
                {MATERIAL_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={fetchReport} disabled={loading || !filters.year || !filters.month}
              className="flex items-center space-x-2 px-6 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run Report'}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Failed to load report</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {hasLoaded && data.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Tons', value: totalTons.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-cyan-600' },
              { label: 'To Company-Owned', value: companyOwnedTons.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-blue-600' },
              { label: 'To Third Party', value: thirdPartyTons.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-purple-600' },
              { label: 'Destinations', value: String(destinationCount), color: 'text-gray-700' },
            ].map(card => (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>
        )}

        {hasLoaded && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {data.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-25" />
                <p className="text-sm font-medium">No data found for the selected filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {['From Facility', 'To Facility', 'To Type', 'Company Owned', 'Material Group', 'Total Tons'].map(col => (
                        <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{row.from_facility_name}</td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.to_facility_name}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{row.to_facility_type || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${row.to_company_owned === 'Y' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                            {row.to_company_owned === 'Y' ? 'Company' : 'Third Party'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.material_group}</td>
                        <td className="px-4 py-3 text-right text-gray-800 tabular-nums font-medium">{toNum(row.total_tons).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!hasLoaded && !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-20">
            <ArrowRightLeft className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-base font-medium text-gray-500">Select filters and click Run Report</p>
            <p className="text-sm text-gray-400 mt-1">Year and Month are required</p>
          </div>
        )}
      </main>
    </div>
  );
}
